import { useState } from "react";

function FAQPage() {
  const sections = [
    {
      title: "About Journey Trails",
      items: [
        {
          q: "What is Journey Trails?",
          a: "Journey Trails is a mindful journaling app for people who work with cannabis or mushrooms and want to track their experiences with intention. It is more than a log. It is a tool for self‑reflection that helps you understand how different strains, types, timing, and other context influence your mood, nervous system, and overall experiences. Whether your journey is deep or you are exploring intentional cannabis sessions, Journey Trails gives you a private space to reflect and learn from your experiences."
        },
        {
          q: "Who is it for?",
          a: "Journey Trails is for adults who incorporate cannabis or mushrooms into their wellness, creative, emotional, or spiritual practices and want to approach those experiences with more intention. If you’re looking for a private, ad‑free space to reflect on your sessions and notice patterns, Journey Trails is designed for you."
        },
        {
          q: "Is it free?",
          a: "Yes, Journey Trails is completely free to use. It’s supported by voluntary donations from people who find value in it. Contributions help cover server costs, hosting, and keeping the app running. If Journey Trails is meaningful to your practice or intentions, any contribution is appreciated."
        },
        {
          q: "What's coming soon?",
          a: "More features and improvements are on the way. Stay tuned."
        },
      ]
    },
    {
      title: "Using the App",
      items: [
        {
          q: "What are Linked Sessions (Chapters)?",
          a: "Linked Sessions are multiple journal entries that belong to the same experience. For cannabis, this might include Before, During, and After entries. When you tap 'Add Entry to Session,' a link available at the bottom of your journal entry form, you are creating another entry linked to the same session, so the full arc of your experience stays organized in one place. You can also link entries via the handy reminder that appears at the top of the entry form showing your most recent session."
        },
        {
          q: "What do Before, During, After, and Integration states mean?",
          a: "These describe when a journal entry was created in relation to your session. Before captures your intentions and baseline state. During captures your in‑the‑moment observations. After captures your reflections and how the experience felt once it concluded. Integration captures any follow‑up insights you choose to record."
        },
        {
          q: "What are journaling prompts?",
          a: "Each session offers a journaling prompt matched to your cannabis strain or mushroom session, session details, and intentions. Prompts are drawn from a hand-crafted library written with depth and intention, with no AI involved. The more you journal, the smarter the matching becomes, surfacing prompts that align with your patterns and practice."
        },
        {
          q: "What are Intention Chips?",
          a: "Intention chips are quick-select tags that let you capture the purpose behind your session, such as Anxiety Relief, Creativity, or Sleep. Over time, these feed into your Insights so you can see which intentions you fulfill most consistently."
        },
        {
          q: "What is Nervous System Tracking?",
          a: "Nervous System Tracking lets you note how activated or settled you feel before and after a session. Journey Trails compares these entries and highlights patterns in your Insights, showing how different contexts relate to your sense of regulation over time."
        },
        {
          q: "What is the Somatic Body Map?",
          a: "The Somatic Body Map is a visual map that lets you tap areas of the body to record where you feel the most awareness or activity during a session, and describe the quality of that experience. These entries help shape your Body Activation Pattern insights over time."
        },
        {
          q: "What is Chakra Check-in?",
          a: "An optional tool to note which energy centers feel active or blocked during your session. For those who work with chakra frameworks, this adds another layer of self-awareness to your journaling."
        },
        {
          q: "How do Insights work?",
          a: "Insights analyze your journal history to surface patterns: which strains improve your mood most, which intentions you fulfill, how your nervous system responds over time, and more. The more you journal, the more meaningful your insights become."
        },
      ]
    },
    {
      title: "Account & Settings",
      items: [
        {
          q: "How do I reset my password?",
          a: "On the login page, click \"Forgot password?\" and enter your email. You'll receive a reset link valid for one hour."
        },
        {
          q: "How do I delete my account?",
          a: "You can permanently delete your account and all associated data from the Settings page. Navigate to Settings and select \"Delete My Account.\" This action is irreversible — all your journal entries, insights, and profile data will be permanently removed."
        },
      ]
    },
    
    {
      title: "Privacy & Data",
      items: [
        {
          q: "Is my data private?",
          a: "Yes. Your journal entries are private and only visible to you. Journey Trails does not sell your data or show you ads."
        },
        {
          q: "Where is my data stored?",
          a: "Your data is securely stored in an encrypted cloud database. It is never shared with third parties."
        },
        {
          q: "Are my journaling prompts private?",
          a: "Yes. Prompts are matched entirely within Journey Trails using your session context, including strain, session type, and intentions. Your journal entry content is never sent anywhere. Everything stays on our servers."
        },
      ]
    },
  ];

  const [openSections, setOpenSections] = useState({ 0: true });

  const toggleSection = (index) => {
    setOpenSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <main>
      <h1>About & FAQ</h1>
      <p style={{ opacity: 0.85, fontSize: "14px", marginBottom: "40px", lineHeight: "1.2" }}>
        Everything you need to know about Journey Trails: what it is, how it works, and what's coming.
      </p>

      {sections.map((section, index) => (
        <div key={section.title} style={{ marginBottom: "12px" }}>

          {/* SECTION HEADER */}
          <div
            onClick={() => toggleSection(index)}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              padding: "12px 0",
              borderBottom: openSections[index]
                ? "1px solid rgba(125, 211, 232, 0.3)"
                : "1px solid rgba(255,255,255,0.08)",
              marginBottom: openSections[index] ? "20px" : "0",
              userSelect: "none",
            }}
          >
            <h2 style={{
              fontSize: "12px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: openSections[index] ? "#7dd3e8" : "rgba(255,255,255,0.6)",
              margin: 0,
              transition: "color 0.2s"
            }}>
              {section.title}
            </h2>
            <span style={{
              fontSize: "12px",
              opacity: 0.5,
              transition: "transform 0.2s",
              transform: openSections[index] ? "rotate(0deg)" : "rotate(-90deg)",
              display: "inline-block"
            }}>
              ▼
            </span>
          </div>

          {/* SECTION CONTENT */}
          {openSections[index] && (
            <div style={{ marginBottom: "24px" }}>
              {section.items.map((item) => (
                <div key={item.q} style={{
                  marginBottom: "24px",
                  paddingLeft: "16px",
                  borderLeft: "2px solid rgba(125, 211, 232, 0.15)"
                }}>
                  <p style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "#e8ecf3",
                    marginBottom: "8px",
                    marginTop: 0
                  }}>
                    {item.q}
                  </p>
                  <p style={{
                    fontSize: "14px",
                    opacity: 0.75,
                    lineHeight: "1.75",
                    margin: 0
                  }}>
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <div style={{
        marginTop: "36px",
        padding: "20px 24px",
        background: "rgba(125, 211, 232, 0.07)",
        border: "1px solid rgba(125, 211, 232, 0.2)",
        borderRadius: "10px",
        fontSize: "14px",
        lineHeight: "1.7",
        opacity: 0.85
      }}>
        💛 Journey Trails is built with care for the community it serves. If you have feedback, ideas, or just want to say hello,{" "}
        <a href="/contact">I'd love to hear from you</a>.
      </div>
    </main>
  );
}

export default FAQPage;